import styled from 'styled-components';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import WaveDivider from '../components/WaveDivider';
import PageHero from '../components/PageHero';

const Section = styled.section`
  padding: ${({ $py }) => $py || '100px'} 24px;
  background: ${({ $bg }) => $bg || 'var(--cream)'};
`;

const Container = styled.div`
  max-width: ${({ $maxWidth }) => $maxWidth || '800px'};
  margin: 0 auto;
`;

const SectionLabel = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ $light }) => $light ? 'var(--sage-light)' : 'var(--sage-muted)'};
  margin-bottom: 16px;
`;

const SectionHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: ${({ $light }) => $light ? 'white' : 'var(--text-dark)'};
  margin-bottom: 32px;
  line-height: 1.3;
`;

const BodyText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: ${({ $light }) => $light ? 'var(--text-light)' : 'var(--text-body)'};
  line-height: 1.8;
  margin-bottom: 20px;
`;

const CompetenceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 32px;
  margin-top: 40px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const CompetenceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-body);
  padding: 8px 0;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background: var(--sage);
`;

const RelationshipList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 24px;
  margin-top: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const RelationshipItem = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: var(--sage-light);
  padding: 4px 0;
`;

const RelationshipLabel = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--sage-light);
  margin-top: 40px;
  margin-bottom: 12px;
`;

const OverviewColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionImage = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(26, 46, 26, 0.06);
`;

const WideImage = styled.img`
  width: 100%;
  max-height: 350px;
  object-fit: cover;
  border-radius: 10px;
  margin: 32px 0;
`;

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 32px;
`;

const Tag = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: var(--spruce);
  background: var(--mist);
  padding: 8px 18px;
  border-radius: 4px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 1px 4px var(--shadow);
  border-left: 3px solid var(--sage);
`;

const CardHeading = styled.h3`
  font-family: 'Lora', serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 16px;
`;

const CardText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-body);
  line-height: 1.7;
`;

const CTASection = styled.section`
  background: var(--mist);
  padding: 100px 24px;
  text-align: center;
`;

const CTAInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTAHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 16px;
`;

const CTASubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
  margin-bottom: 32px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--sage);
  color: white;
  padding: 14px 36px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.8rem;
  transition: background 0.3s ease;

  &:hover {
    background: var(--sage-muted);
  }
`;

const competences = [
  'Working with queer and trans people',
  'Relationships (of any configuration!)',
  'Acute and complex trauma',
  'ADHD/Autism/Neurodivergence',
  'Grief',
  'Chronic illness',
  'Perinatal/parental transition work',
];

const relationshipTypes = [
  'Couples', 'Polycules', 'Parents and Children',
  'Coworkers', 'Chosen families', 'Roommates',
  'Siblings', 'Friendships', 'Coparents',
];

const individualTags = [
  'Grief', 'Trauma', 'Life transitions', 'Anxiety',
  'Neurodivergence', 'Intellectualization', 'Nervous system regulation',
  'Presence with your own body', 'Understanding yourself or parts of yourself',
];

const Services = () => {
  useScrollReveal();

  return (
    <>
      <PageHero
        label="Services Offered"
        title="Therapy Services"
        subtitle="Talk therapy, somatic therapy, and EMDR — woven together in whatever combination serves you best."
      />

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      {/* Overview */}
      <Section $bg="var(--cream)">
        <Container $maxWidth="1000px" className="fade-in">
          <OverviewColumns>
            <div>
              <BodyText>
                I work with individual adults and teenagers, as well as relationship
                configurations of all kinds. I work to create an environment that is
                kind and challenging, offering both encouraging support and direct
                feedback. My goal in therapy is to collaboratively create a space in
                which we can rework patterned behaviour and experience new ways of
                relating.
              </BodyText>
              <BodyText>
                I offer talk therapy, somatic therapy, and EMDR therapy. These can be combined or woven together in any combination.
              </BodyText>
              <SectionLabel style={{ marginTop: '40px' }}>Areas of Competence</SectionLabel>
              <CompetenceGrid>
                {competences.map((item) => (
                  <CompetenceItem key={item}>
                    <Dot />
                    {item}
                  </CompetenceItem>
                ))}
              </CompetenceGrid>
            </div>
            <SectionImage
              src="/images/calm-water.jpg"
              alt="Peaceful pond reflecting green forest"
            />
          </OverviewColumns>
        </Container>
      </Section>

      <WaveDivider topColor="#eef0eb" bottomColor="#1a2e1a" />

      {/* Relationship Therapy */}
      <Section $bg="var(--spruce-dark)">
        <Container className="fade-in">
          <SectionLabel $light>Relationship Therapy</SectionLabel>
          <SectionHeading $light>Feel Closer, Safer, and More at Home</SectionHeading>
          <BodyText $light>
            Relationships are a massively important part of life. I come to
            relationship-based therapy (often referred to as Couples Therapy) with an openness to working on whatever
            relationships feel important enough to you to put in the time and
            effort to make better. I am LGBTQ+, ENM, and kink-aligned, and
            welcome all expressions of consenting relationship.
          </BodyText>
          <BodyText $light>
            My goal in relationship work is to create a safe container to explore
            the painful spots in your relationship. This includes identifying
            what's not working as well as highlighting and celebrating what is
            working well. I want to help connect you to the strength of your
            relationship.
          </BodyText>
          <BodyText $light>
            I find it's really important in this work to focus on dynamics —
            it's not what you're doing or the other is doing (although that is
            part of it), but how what you're doing and what the other is doing
            are playing off of each other. If we can really understand this
            dance, why it happens and what all the steps are, we can start to
            enact some change. An important question to ask yourself before starting relationship therapy is "What am I contributing to this dynamic? What might I be willing to change?"
          </BodyText>
          <BodyText $light>
            I believe that a relationship ending is not a failure, and the goal
            of relationship therapy should not be to stay together at all
            costs. Instead, my goal is to help you exist in the kindest and
            happiest version of this relationship, however that may look.
          </BodyText>
          <RelationshipLabel>Types of relationships I work with:</RelationshipLabel>
          <RelationshipList>
            {relationshipTypes.map((type) => (
              <RelationshipItem key={type}>{type}</RelationshipItem>
            ))}
          </RelationshipList>
        </Container>
      </Section>

      <WaveDivider topColor="#1a2e1a" bottomColor="#e4e8e1" />

      {/* Individual Therapy */}
      <Section $bg="var(--frost)">
        <Container className="fade-in">
          <SectionLabel>Individual Therapy</SectionLabel>
          <SectionHeading>Find Your Way Forward</SectionHeading>
          <BodyText>
            Do you feel stuck, tired, and uncertain of where to start?
          </BodyText>
          <BodyText>
            Starting therapy can feel daunting. It takes a lot of energy and
            vulnerability to jump into this kind of work with someone who is
            effectively a stranger to you. I don't take that lightly.
          </BodyText>
          <BodyText>
            While I specialize in relationship therapy, I do work with
            individuals as well. I practice from a trauma-informed,
            anti-oppressive lens. This means that I work to create a space that
            highlights your choice, agency, and control. I work to help you have
            a different experience with the pieces of your life that are causing
            you pain, and to gain trust and compassion for yourself.
          </BodyText>
          <BodyText>
            I use humor and warmth to explore patterns and help identify things
            you might have trouble seeing. I am often described as curious,
            compassionate, and straightforward.
          </BodyText>
          <WideImage
            src="/images/vermont-lake-fall.jpg"
            alt="Vermont fall foliage reflecting on a calm lake"
          />
          <SectionLabel style={{ marginTop: '32px' }}>
            Individual therapy can support you with:
          </SectionLabel>
          <TagsWrapper>
            {individualTags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </TagsWrapper>
        </Container>
      </Section>

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      {/* EMDR & Groups */}
      <Section $bg="var(--cream)" $py="80px">
        <Container $maxWidth="1000px" className="fade-in">
          <TwoColumnGrid>
            <Card>
              <CardHeading>EMDR Therapy</CardHeading>
              <CardText>
                I am also able to provide EMDR either as primary therapy or as adjunct therapy to support work with your primary therapist. I am able to weave EMDR into our therapy even if it is not our primary focus.
              </CardText>
            </Card>
            <Card>
              <CardHeading>Groups</CardHeading>
              <CardText>
                At this time all groups are currently full and running. Please
                check back here in the future for further offerings.
              </CardText>
            </Card>
          </TwoColumnGrid>
        </Container>
      </Section>

      <WaveDivider topColor="#eef0eb" bottomColor="#d8ddd5" />

      {/* CTA */}
      <CTASection>
        <CTAInner className="fade-in">
          <CTAHeading>Ready to Begin?</CTAHeading>
          <CTASubtitle>
            Reach out to schedule a free 15-minute phone consultation.
          </CTASubtitle>
          <CTAButton to="/contact">Book a Consultation</CTAButton>
        </CTAInner>
      </CTASection>
    </>
  );
};

export default Services;
